import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, UserRound, Menu, X } from 'lucide-react';
import { useAuthModal } from '../context/AuthModalContext';
import { useAuth } from '../context/AuthContext';
import { MPL, MPL_FONT_BODY, MPL_FONT_DISPLAY, PawMark } from '../styles/mypetlive';
import { isNativeApp } from '../utils/native';
import { Header as AppHeader } from '../layout/AppShell';

export default function PublicHeader() {
  const { openAuth } = useAuthModal();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  // En la app el menú solo llevaba a secciones de la portada web (cómo
  // funciona, impacto) y la navegación ya está en la barra inferior.
  const native = isNativeApp();
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.key]);
  const panelPath = user?.role === 'tenant'
    ? '/home'
    : user?.role === 'landlord'
      ? '/landlord'
      : user?.role === 'admin'
        ? '/admin'
        : user?.role === 'store' || user?.role === 'vet'
          ? '/partner'
          : '/profile';

  // Con sesión iniciada, la misma cabecera que el resto de la app (Patitas, QR y
  // menú). Antes «Compañeros», la ficha de un animal y Favoritos enseñaban esta,
  // la pública, y al entrar en ellas la cabecera cambiaba (aviso de Jorge, 2 nov 2026).
  const conSesion = Boolean(user);
  if (conSesion) return <AppHeader sinMenuLateral />;

  return (
    <div
      className="safe-top"
      onKeyDown={event => { if (event.key === 'Escape') setMenuOpen(false); }}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'rgba(246,243,236,.97)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${MPL.border}`,
      }}
    >
      <style>{`
        .public-header a{color:inherit;text-decoration:none;}
        .public-header-link:hover{color:${MPL.teal}!important;}
        .public-header-cta:hover{filter:brightness(1.05);}
        .public-header-menu-button{display:none!important;}
        .public-header-mobile-menu{display:none;}
        @media (max-width: 900px){
          .public-header-nav{display:none!important;}
          .public-header-menu-button{display:inline-flex!important;}
          .public-header-mobile-menu{display:flex;flex-wrap:wrap;gap:16px;padding:16px 20px;font-family:${MPL_FONT_BODY};background:${MPL.bg};border-top:1px solid ${MPL.border};}
          .public-header-mobile-menu a{color:${MPL.tealDark};font-weight:700;text-decoration:none;padding:8px;}
          .public-header-topbar{width:100%!important;height:56px;padding:0 16px!important;gap:8px;}
          .public-header-cta{display:none!important;}
        }
        @media (max-width: 520px){
          .public-header-account-name{display:none;}
        }
      `}</style>
      <div
        className="public-header public-header-topbar"
        style={{
          maxWidth: 1180,
          width: '100%',
          boxSizing: 'border-box',
          margin: '0 auto',
          padding: '15px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: MPL_FONT_BODY,
          color: MPL.ink,
        }}
      >
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 9 }} aria-label="MyPetLive inicio">
          <span style={{ color: MPL.teal, display: 'inline-flex' }}>
            <PawMark size={24} />
          </span>
          <span style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 20, fontWeight: 800 }}>
            MyPet<span style={{ color: MPL.coral }}>Live</span>
          </span>
        </Link>
        <div
          className="public-header-nav"
          style={{ display: 'flex', gap: 28, fontSize: 14.5, fontWeight: 600, color: MPL.muted, alignItems: 'center' }}
        >
          <Link className="public-header-link" to="/animals">Adoptar</Link>
          <Link className="public-header-link" to="/#como">Cómo funciona</Link>
          <Link className="public-header-link" to="/#impacto">Impacto</Link>
          <button
            className="public-header-link"
            // Prometía "Crea tu cuenta de protectora" y abría un registro que
            // solo da de alta adoptantes. Las cuentas profesionales las
            // activamos a mano, así que se ofrece entrar o solicitar el alta.
            onClick={() => openAuth({ mode: 'login', message: 'Las cuentas de protectora las activamos nosotros. Si ya la tienes, entra aquí; si no, escríbenos a soporte@mypetlive.es y verificamos la entidad.' })}
            style={{ background: 'none', border: 'none', font: 'inherit', cursor: 'pointer', color: MPL.muted }}
          >
            Protectoras
          </button>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {user ? (
            <>
              <Link className="public-header-login" to={panelPath} aria-label="Mi panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 700 }}>
                <span style={{ width: 34, height: 34, borderRadius: 11, display: 'grid', placeItems: 'center', background: MPL.teal100, color: MPL.teal }}>
                  <UserRound size={17} />
                </span>
                <span className="public-header-account-name">{user.name || 'Mi panel'}</span>
              </Link>
              <button type="button" onClick={logout} aria-label="Cerrar sesión" title="Cerrar sesión" style={{ padding: 0, width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 12, border: `1px solid ${MPL.border}`, background: '#fff', color: MPL.muted, cursor: 'pointer' }}>
                <LogOut size={17} />
              </button>
            </>
          ) : (
            <>
              <button
                className="public-header-link public-header-login"
                onClick={() => openAuth({ mode: 'login' })}
                style={{ background: 'none', border: 'none', font: 'inherit', cursor: 'pointer', fontSize: 14.5, fontWeight: 600, color: MPL.ink }}
              >
                Entrar
              </button>
              <Link
                className="public-header-cta"
                to="/animals"
                style={{
                  background: MPL.coral,
                  color: '#fff',
                  fontSize: 14.5,
                  fontWeight: 700,
                  padding: '11px 22px',
                  borderRadius: 14,
                  boxShadow: '0 6px 16px -8px rgba(232,101,74,.7)',
                }}
              >
                Adoptar
              </Link>
            </>
          )}
          {!native && <button type="button" className="public-header-menu-button" aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={menuOpen} aria-controls="public-mobile-menu" onClick={() => setMenuOpen(open => !open)} style={{ width: 40, height: 40, padding: 0, flexShrink: 0, alignItems: 'center', justifyContent: 'center', border: `1px solid ${MPL.border}`, borderRadius: 12, background: '#fff', color: MPL.ink }}>
            {menuOpen ? <X size={22} style={{ flexShrink: 0 }} /> : <Menu size={22} style={{ flexShrink: 0 }} />}
          </button>}
        </div>
      </div>
      {menuOpen && <nav id="public-mobile-menu" className="public-header-mobile-menu" aria-label="Menú principal">
        <Link to="/animals">Adoptar</Link>
        <Link to="/#como">Cómo funciona</Link>
        <Link to="/#impacto">Impacto</Link>
        <a href="mailto:soporte@mypetlive.es?subject=Alta%20profesional">Alta profesional</a>
        <Link to="/legal/privacy">Privacidad</Link>
      </nav>}
    </div>
  );
}
